import { useState } from "react";
import {
  AlertTriangle,
  Loader2,
  Trash2,
  UserMinus,
  UserPlus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BulkActionsBar as Shell } from "@/components/ui/BulkActionsBar";
import {
  bulkDeleteContacts,
  bulkUpdateContacts,
} from "@/api/contacts";

interface ContactsBulkActionsBarProps {
  projectSlug: string;
  selectedIds: number[];
  canEdit: boolean;
  canDelete: boolean;
  onClear: () => void;
  /** Called after a successful bulk update; receives the partial fields applied. */
  onBulkUpdated: (
    ids: number[],
    update: { is_decision_maker?: boolean },
  ) => void;
  /** Called after a successful bulk delete. */
  onBulkDeleted: (ids: number[]) => void;
}

type Busy = null | "dm-on" | "dm-off" | "delete";

export function ContactsBulkActionsBar({
  projectSlug,
  selectedIds,
  canEdit,
  canDelete,
  onClear,
  onBulkUpdated,
  onBulkDeleted,
}: ContactsBulkActionsBarProps) {
  const [busy, setBusy] = useState<Busy>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const count = selectedIds.length;

  async function setDM(value: boolean) {
    setBusy(value ? "dm-on" : "dm-off");
    try {
      const res = await bulkUpdateContacts(projectSlug, selectedIds, {
        is_decision_maker: value,
      });
      onBulkUpdated(selectedIds, { is_decision_maker: value });
      toast.success(
        value
          ? `${res.updated} marked as decision maker`
          : `${res.updated} unmarked as decision maker`,
      );
      onClear();
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function confirmDelete() {
    setBusy("delete");
    try {
      const res = await bulkDeleteContacts(projectSlug, selectedIds);
      onBulkDeleted(selectedIds);
      toast.success(
        `${res.deleted} contact${res.deleted > 1 ? "s" : ""} deleted`,
      );
      setConfirmOpen(false);
      onClear();
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Shell count={count} onClear={onClear}>
        {canEdit && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDM(true)}
              disabled={busy !== null}
            >
              {busy === "dm-on" ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-3.5 w-3.5" />
              )}
              Mark decision maker
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDM(false)}
              disabled={busy !== null}
            >
              {busy === "dm-off" ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserMinus className="mr-2 h-3.5 w-3.5" />
              )}
              Remove decision maker
            </Button>
          </>
        )}

        {canDelete && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={busy !== null}
            className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </Button>
        )}
      </Shell>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (busy === "delete") return;
          setConfirmOpen(o);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              Delete {count} contact{count > 1 ? "s" : ""}?
            </DialogTitle>
            <DialogDescription>
              You are about to permanently remove the selected contacts.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <p className="text-xs leading-relaxed text-rose-700">
              This action is irreversible. Email, phone, LinkedIn and any
              related activity for{" "}
              {count > 1 ? "these contacts" : "this contact"} will be removed.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={busy === "delete"}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={busy === "delete"}
            >
              {busy === "delete" && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Bulk action failed.";
}
